import {
  Box,
  Button,
  ColumnLayout,
  Container,
  Form,
  FormField,
  Header,
  Icon,
  Select,
  SelectProps,
  SpaceBetween,
  Spinner,
  TextContent,
  Checkbox,
} from "@cloudscape-design/components-themed";
import { ClickDetail } from "@cloudscape-design/components-themed/internal/events";
import {
  ChangeEvent,
  createRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAppDispatch } from "../../app/hook";
import { useCanIShowHook } from "../../app/useCanIShowHook";
import { USER_TYPE, PERMISSIONS } from "../../configs/constants";
import {
  useGetAuthQuery,
  useGetComprehensiveCompaniesQuery,
  usePostMerchantsInclusionApplicationUploadMutation,
  usePostCompanyCodeCheckMutation,
} from "../../features/api";
import { useHasAuthorityHook } from "../../app/useHasAuthorityHook";
import { ComprehensiveCompanySearchRequest } from "../../features/api-interfaces";
import { Messages, getMessage } from "../../features/error/messages";
import { addNotification } from "../../features/error/notifications-slice";
import { getExt } from "../../utils/common";
import { downloadBase64File } from "../../utils/downLoad";
import { formatLabel } from "../../utils/format";
import { isNull } from "../../utils/inputValidation";
import { FileUpload } from "../UI/file-upload/FileUpload";
import { showModal } from "../UI/modal/modal-slice";
import ErrorTable, {
  MessageItem,
  getMessageItemList,
} from "../UI/table/ErrorTable";
import CompanyCodeErrorTable, {
  CheckResult,
  getErrorMessageItemList,
} from "../UI/table/CompanyCodeErrorTable";
import { COMMON_LABELS, COMMON_CONFIGS } from "../default-constants";
import {
  ARROW_STYLE,
  APPLICATION_NOTICE,
  CORPORATE_APPLICATION_CHANGE_MODAL_NOTICE,
  CONFIGS,
  CONTENTS,
  LABELS,
  CORPORATE_APPLICATION_CHANGE_NOTICE,
  CORPORATE_APPLICATION_CHANGE_MODAL_NOTICE_TITLE,
  CHECK_TARGET_MERCHANT_STORE,
  CANCEL_NOTICE,
  CANCEL_NOTICE_CONTENT,
  UPDATE_DIFFERENCIAL_INFO,
} from "./constants";
import { getComprehensiveCompanyItems } from "./util";
import MultilineText from "../UI/MultilineText";
import { ulFileCheck } from "../../utils/fileControl";
import { UPDATE_DIFFERENCIAL } from "../../configs/constants";

/**
 * 加盟店申請（包括）画面
 */
export const Main = () => {
  const dispatch = useAppDispatch();
  const isDocomoUser = useCanIShowHook(USER_TYPE.DOCOMO);
  const isApply = !!useHasAuthorityHook(PERMISSIONS.APPLY);
  const isMasterApproval = !!useHasAuthorityHook(PERMISSIONS.MASTER_APPROVAL);
  const isDocomoMasterUser = isDocomoUser && isMasterApproval && isApply;
  const corporateInputRef = useRef<HTMLInputElement>(null);
  const storeInputRef = useRef<HTMLInputElement>(null);
  const corporateErrorRef = createRef<HTMLDivElement>();
  const storeErrorRef = createRef<HTMLDivElement>();
  const [contractRouteErrorText, setContractRouteErrorText] = useState("");
  const [selectItemList, setSelectItemList] = useState<SelectProps.Option[]>(
    []
  );
  const [selectedCompanyCode, setSelectedCompanyCode] =
    useState<SelectProps.Option>({
      label: "",
      value: "",
    });
  const [uploadCorporateFile, setUploadCorporateFile] = useState<
    File | undefined
  >(undefined);
  const [corporateErrorText, setCorporateErrorText] = useState("");
  const [uploadStoreFile, setUploadStoreFile] = useState<File | undefined>(
    undefined
  );
  const [storeErrorText, setStoreErrorText] = useState("");
  const [isSpinnerDisplay, setIsSpinnerDisplay] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [maintenanceFlg, setMaintenanceFlg] = useState(false);
  const [corporateErrorMessageList, setCorporateErrorMessageList] = useState<
    MessageItem[]
  >([]);
  const [storeErrorMessageList, setStoreErrorMessageList] = useState<
    MessageItem[]
  >([]);
  const { companyCode } = useGetAuthQuery(undefined, {
    selectFromResult: ({ data }) => ({
      companyCode: data?.companyCode,
    }),
  });
  const { data } = useGetComprehensiveCompaniesQuery({
    companyCode,
    invisibleComprehensiveCompanyNoList: [3], // メルペイを除外
  } as ComprehensiveCompanySearchRequest);
  const [uploadMerchantsInclusionApplicationFiles] =
    usePostMerchantsInclusionApplicationUploadMutation();
  const [companyCodeCheck] = usePostCompanyCodeCheckMutation();

  // このコールバックを呼び出して ref.current.scrollIntoView() を呼び出してスクロール
  const scrollToBottomOfList = useCallback(() => {
    // 店舗用エラー内容にスクロール
    storeErrorRef?.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
    // 法人用エラー内容にスクロール
    corporateErrorRef?.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corporateErrorRef, storeErrorRef]);

  useEffect(() => {
    // ページが描画されたらリストの末尾までスクロール
    scrollToBottomOfList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corporateErrorMessageList, storeErrorMessageList]);

  useEffect(() => {
    setSelectItemList(getComprehensiveCompanyItems(data));
  }, [data]);

  /**
   * 法人提出用ファイルを設定する.
   * @param {ChangeEvent<HTMLInputElement>} event
   */
  const onChangeCorporateFile = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files?.[0]) {
      return;
    }
    if (CONFIGS.FILE_EXTENSION !== getExt(files[0].name.toLowerCase())) {
      dispatch(
        addNotification({
          msgId: "KGRERR057",
          msgArgs: [],
        })
      );
      return;
    }
    setUploadCorporateFile(files[0]);
    setCorporateErrorText(LABELS.ERROR_TEXT_LABEL);
  };

  /**
   * 店舗提出用ファイルを設定する.
   * @param {ChangeEvent<HTMLInputElement>} event
   */
  const onChangeStoreFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files?.[0]) {
      return;
    }
    if (CONFIGS.FILE_EXTENSION !== getExt(files[0].name.toLowerCase())) {
      dispatch(
        addNotification({
          msgId: "KGRERR057",
          msgArgs: [],
        })
      );
      return;
    }
    setUploadStoreFile(files[0]);
    setStoreErrorText(LABELS.ERROR_TEXT_LABEL);
  };

  /**
   * 法人用アップロードボタンイベント.
   * @param {CustomEvent<ClickDetail>} event
   */
  const corporateUploadOnClick = (event: CustomEvent<ClickDetail>) => {
    event.preventDefault();
    corporateInputRef.current?.click();
  };

  /**
   * 店舗用アップロードボタンイベント.
   * @param {CustomEvent<ClickDetail>} event
   */
  const storeUploadOnClick = (event: CustomEvent<ClickDetail>) => {
    event.preventDefault();
    storeInputRef.current?.click();
  };

  /**
   * 申請ボタンを押下したときの処理.
   */
  const onClickButton = async () => {
    if (isInputError()) {
      return;
    }

    if (maintenanceFlg) {
      // メンテナンス時はメンテナンス確認用のダイアログを表示
      dispatch(
        showModal({
          title: COMMON_LABELS.TITLES.DL,
          content: COMMON_LABELS.CONTENTS.MAINTENANCE_NOTICE,
          negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
          positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
          positiveFn: () => {
            confirmExecute();
          },
        })
      );
    } else {
      // それ以外は通常ダイアログ
      confirmExecute();
    }
  };

  /**
   * 契約ルートとファイルアップロードの入力チェック.
   * @returns {boolean} true: 入力エラー
   */
  const isInputError = (): boolean => {
    let isError = false;

    if (isNull(selectedCompanyCode.value as string) && isDocomoUser) {
      setContractRouteErrorText(
        getMessage("KGRERR023", [LABELS.CONTRACT_ROUTE]).label
      );
      isError = true;
    }

    if (uploadCorporateFile === undefined && uploadStoreFile === undefined) {
      const messageId = "KGRERR054" as keyof Messages;
      setCorporateErrorText(getMessage(messageId).label);
      setStoreErrorText(getMessage(messageId).label);
      isError = true;
    }

    return isError;
  };

  /**
   * ファイルアップロード実行確認
   */
  const confirmExecute = async () => {
    const result = await dataCheck();

    if (!result.isError) {
      return;
    }

    const fileData = new FormData();
    //  申請書に解約申請を含む場合はtrue
    const cancelIncludeFlag = await ulFileCheck(
      uploadCorporateFile,
      UPDATE_DIFFERENCIAL_INFO.ROW_NO,
      UPDATE_DIFFERENCIAL_INFO.COL_NO,
      UPDATE_DIFFERENCIAL_INFO.SHEET_NAME,
      UPDATE_DIFFERENCIAL.CANCELLATION
    );

    let contentMessage = COMMON_LABELS.CONTENTS.APPLICATION_CONFIRM;
    const comprehensiveCompanyCode = isDocomoUser
      ? selectedCompanyCode.value!
      : companyCode!;
    fileData.append(CONFIGS.COMPANY_CODE, comprehensiveCompanyCode);
    fileData.append(CONFIGS.MAINTENANCE_FLG, maintenanceFlg ? "true" : "false");
    const existCorporateFile = uploadCorporateFile !== undefined;

    if (existCorporateFile) {
      fileData.append(CONFIGS.FILE, uploadCorporateFile);
      fileData.append(CONFIGS.UPLOAD_TYPE, CONFIGS.CORPORATION);
      contentMessage += formatLabel(CONTENTS.APPLICATION_CONFIRM_CORPORATE, [
        uploadCorporateFile.name,
      ]);
    }

    if (uploadStoreFile !== undefined) {
      fileData.append(CONFIGS.FILE, uploadStoreFile);
      fileData.append(CONFIGS.UPLOAD_TYPE, CONFIGS.STORE);
      contentMessage += formatLabel(CONTENTS.APPLICATION_CONFIRM_STORE, [
        uploadStoreFile.name,
      ]);
    }

    setTimeout(() => {
      dispatch(
        showModal({
          title: COMMON_LABELS.TITLES.DL,
          content: (
            <>
              {cancelIncludeFlag && (
                <>
                  <SpaceBetween size="xxxs" direction="horizontal">
                    <Icon name="status-warning" variant="error" />
                    <Box color="text-status-error">{CANCEL_NOTICE}</Box>
                    <Icon name="status-warning" variant="error" />
                  </SpaceBetween>
                  <MultilineText
                    content={CANCEL_NOTICE_CONTENT}
                    boxProps={{ color: "text-status-error" }}
                  />
                </>
              )}
              {existCorporateFile && (
                <>
                  <SpaceBetween size="xxxs" direction="horizontal">
                    <Icon name="status-warning" variant="error" />
                    <Box color="text-status-error">
                      {CORPORATE_APPLICATION_CHANGE_MODAL_NOTICE_TITLE}
                    </Box>
                    <Icon name="status-warning" variant="error" />
                  </SpaceBetween>
                  <MultilineText
                    content={CORPORATE_APPLICATION_CHANGE_MODAL_NOTICE}
                    boxProps={{ color: "text-status-error" }}
                  />
                </>
              )}
              <MultilineText content={contentMessage} />
              {result.itemList.length > 0 && (
                <>
                  <br />
                  <CompanyCodeErrorTable data={result.itemList} />
                </>
              )}
            </>
          ),
          negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
          positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
          positiveFn: () => {
            merchantApplication(fileData);
          },
        })
      );
    }, 0);

    // 同じファイルを連続アップロードできるように処理
    if (corporateInputRef.current) {
      corporateInputRef.current.value = "";
    }

    if (storeInputRef.current) {
      storeInputRef.current.value = "";
    }
  };

  /**
   * 確認ダイアログでOKボタンを押したときの処理
   * @param {FormData} fileData ファイル
   */
  const merchantApplication = async (fileData: FormData) => {
    setIsSpinnerDisplay(true);
    setIsButtonDisabled(true);
    setStoreErrorText("");
    setCorporateErrorText("");

    uploadMerchantsInclusionApplicationFiles(fileData)
      .unwrap()
      .then((res) => {
        setCorporateErrorMessageList([]);
        setStoreErrorMessageList([]);
        setSelectedCompanyCode({
          label: "",
          value: "",
        });
        setUploadCorporateFile(undefined);
        setUploadStoreFile(undefined);

        if (COMMON_CONFIGS.API.STATUS_ERROR === res.status) {
          if (
            res.corporateErrorMessageList !== null &&
            res.corporateErrorMessageList.length !== 0
          ) {
            setCorporateErrorMessageList(
              getMessageItemList(res.corporateErrorMessageList)
            );
          }
          if (
            res.storeErrorMessageList !== null &&
            res.storeErrorMessageList.length !== 0
          ) {
            setStoreErrorMessageList(
              getMessageItemList(res.storeErrorMessageList)
            );
          }
          if (res.items) {
            res.items.forEach((item) => downloadBase64File(item));
          }
        }
      })
      .finally(() => {
        setIsSpinnerDisplay(false);
        setIsButtonDisabled(false);
      });
  };

  /**
   * 申請ボタン押下後の企業コードチェック
   */
  const dataCheck = async (): Promise<CheckResult> => {
    const result: CheckResult = { isError: true, itemList: [] };

    if (uploadStoreFile === undefined) {
      return result;
    }

    const fileData = new FormData();

    fileData.append(CONFIGS.TARGET, CHECK_TARGET_MERCHANT_STORE);
    fileData.append(CONFIGS.FILE, uploadStoreFile);

    setIsSpinnerDisplay(true);
    setIsButtonDisabled(true);

    await companyCodeCheck(fileData)
      .unwrap()
      .then((res) => {
        if (COMMON_CONFIGS.API.STATUS_ERROR === res.status) {
          result.isError = false;
        }
        if (res.errorInfos !== null) {
          result.itemList = getErrorMessageItemList(res.errorInfos);
        }
      })
      .finally(() => {
        setIsSpinnerDisplay(false);
        setIsButtonDisabled(false);
      });

    return result;
  };

  return (
    <Container
      header={
        <Header variant="h2" description="">
          {LABELS.APPLICATION_FILE}
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="xxl">
        <Form
          actions={
            <SpaceBetween direction="horizontal" size="l">
              {isSpinnerDisplay && <Spinner size="big" />}
              <Button
                iconAlign="right"
                onClick={onClickButton}
                variant="primary"
                disabled={isButtonDisabled}
              >
                {COMMON_LABELS.BTNS.APPLY}
              </Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween direction="vertical" size="xxl">
            {isDocomoUser && (
              <ColumnLayout columns={4}>
                <FormField
                  label={
                    <SpaceBetween direction="horizontal" size="xs">
                      <Box variant="strong">{LABELS.CONTRACT_ROUTE}</Box>
                      <Box variant="small" color="text-status-error">
                        {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
                      </Box>
                    </SpaceBetween>
                  }
                  errorText={contractRouteErrorText}
                >
                  <Select
                    selectedOption={selectedCompanyCode}
                    options={selectItemList}
                    filteringType="auto"
                    onChange={(event: any) => {
                      setContractRouteErrorText("");
                      setSelectedCompanyCode(event.detail.selectedOption);
                    }}
                  />
                </FormField>
              </ColumnLayout>
            )}
            <FileUpload
              inputRef={corporateInputRef}
              label={LABELS.FOR_CORPORATION}
              name={"corporateFile"}
              placeholder={LABELS.FILE_PLACEHOLDER}
              accept={CONFIGS.FILE_EXTENSION}
              fileName={uploadCorporateFile ? uploadCorporateFile.name : ""}
              setUploadFile={setUploadCorporateFile}
              errorText={corporateErrorText}
              setErrorText={setCorporateErrorText}
              onChange={onChangeCorporateFile}
              onUpload={corporateUploadOnClick}
            />
            <FileUpload
              inputRef={storeInputRef}
              label={LABELS.FOR_STORE}
              name={"storeFile"}
              placeholder={LABELS.FILE_PLACEHOLDER}
              accept={CONFIGS.FILE_EXTENSION}
              fileName={uploadStoreFile ? uploadStoreFile.name : ""}
              setUploadFile={setUploadStoreFile}
              errorText={storeErrorText}
              setErrorText={setStoreErrorText}
              onChange={onChangeStoreFile}
              onUpload={storeUploadOnClick}
            />
            {isDocomoMasterUser && (
              <Checkbox
                checked={maintenanceFlg}
                onChange={({ detail: { checked } }) => {
                  setMaintenanceFlg(checked);
                }}
              >
                {LABELS.MAINTENANCE_LABEL}
              </Checkbox>
            )}
          </SpaceBetween>
        </Form>
        <SpaceBetween direction="vertical" size="xxs">
          <Container>
            <MultilineText content={APPLICATION_NOTICE} />
            <MultilineText
              content={CORPORATE_APPLICATION_CHANGE_NOTICE}
              boxProps={{ color: "text-status-error" }}
            />
          </Container>
          <TextContent>
            <p>
              詳細につきましては、メニューバー「コミュニケーション等」
              <span style={ARROW_STYLE}>-&gt;</span>
              「ドキュメント一覧」よりd払い加盟店サイト操作マニュアルをご確認ください。
            </p>
          </TextContent>
        </SpaceBetween>
        <SpaceBetween direction="vertical" size="xxs">
          {corporateErrorMessageList.length !== 0 && (
            <>
              <ErrorTable
                data={corporateErrorMessageList}
                title={LABELS.ERROR_TITLE_FOR_CORPORATION}
              />
              <div id="corporateError" ref={corporateErrorRef} />
            </>
          )}
          {storeErrorMessageList.length !== 0 && (
            <>
              <ErrorTable
                data={storeErrorMessageList}
                title={LABELS.ERROR_TITLE_FOR_STORE}
              />
              <div id="storeError" ref={storeErrorRef} />
            </>
          )}
        </SpaceBetween>
      </SpaceBetween>
    </Container>
  );
};
